import React, { useRef, useState } from 'react';
import { connect } from 'react-redux';

import { toggleModal } from 'apps/auth/actions/appAction';
import { changeFormField } from 'apps/auth/actions/formAction';
import {
    CalculatorAmountBlock,
    CalculatorFrame,
    CalculatorFrameBottom,
    CalculatorFrameTop,
    CalculatorInputLabel,
    Helper,
} from 'apps/auth/components/common/Calculator/styles';
import { DiskBadge } from 'apps/auth/components/common/DiskBadge/DiskBadge';
import { DiskButton } from 'apps/auth/components/common/DiskButton/DiskButton';
import { Icon } from 'apps/auth/components/common/Icon/Icon';
import { Panel } from 'apps/auth/components/common/Panel/Panel';
import { DiskBadgeWrapper, PanelRightBlock, PanelRow } from 'apps/auth/components/common/Panel/styles';
import PopupNew from 'apps/auth/components/common/Popup/PopupNew';
import { Text } from 'apps/auth/components/common/Text/Text';
import InputAmount from 'apps/auth/components/form-multi/InputAmount';
import ModalComponent from 'apps/auth/components/utils/ModalComponent';
import { getLocalizedRawAmount } from 'apps/auth/helpers/formats';
import { getBankAlias, resolveArguments } from 'apps/auth/helpers/utils';
import { useApp, useExtra, useLanguage, usePageMessages } from 'apps/auth/hooks/selectors';

import TopUpMethodList from './TopUpMethodList';
import TopUpLimitsPopup from '../popups/TopUpLimitsPopup';

const TopUpCalc = ({ ...props }) => {
    const { country } = useApp();
    const { lang } = useLanguage();
    const popupRef = useRef(null);
    const [amountFromFloatValue, setAmountFromFloatValue] = useState();

    const { account, minAmount, maxAmount, linkedCards } = useExtra();
    const { topUpPayTitle, newBalance } = usePageMessages();

    const labelClickHandler = (e) => {
        e.preventDefault();
        props.toggleModal(
            true,
            <ModalComponent isIconClose>
                <TopUpMethodList
                    amount={props.amountFrom.value}
                    currency={account.pocketCurrency.code}
                    linkedCards={linkedCards}
                    currentCard={props.sourceId}
                />
            </ModalComponent>,
        );
    };

    const getLabel = () => {
        if (props.card) {
            return (
                <Panel isBordered hasBorderRadius isHoverable onClick={labelClickHandler}>
                    <PanelRow>
                        <PanelRow alignStart>
                            <DiskBadgeWrapper>{getBankAlias(props.card.cardLogo)}</DiskBadgeWrapper>
                            {props.card.alias} ·· {props.card.description}
                        </PanelRow>
                        <PanelRightBlock>
                            <Text color="secondary" smallCaps>
                                <Icon name="chevron-down" />
                            </Text>
                        </PanelRightBlock>
                    </PanelRow>
                </Panel>
            );
        } else {
            return (
                <Panel isBordered hasBorderRadius isHoverable onClick={labelClickHandler}>
                    <PanelRow>
                        <PanelRow alignStart>
                            <DiskBadgeWrapper>
                                <DiskBadge variant="secondary" iconName="bank" />
                            </DiskBadgeWrapper>
                            {props.itemTitle}
                        </PanelRow>
                        <PanelRightBlock>
                            <Text color="secondary" smallCaps>
                                <Icon name="chevron-down" />
                            </Text>
                        </PanelRightBlock>
                    </PanelRow>
                </Panel>
            );
        }
    };

    const getSecondaryText = () => {
        const currentAmount = account.balance;
        const amountFrom = props.amountFrom;
        const amountCurrency = account.pocketCurrency.code;
        if (amountFrom.value) {
            const amountFromValue = amountFromFloatValue;
            if (amountFrom.isError) {
                const isShowIcon = parseFloat(amountFromValue) > parseFloat(maxAmount.amount);
                return (
                    <Helper noPadding>
                        <Text size="secondary" color="error">
                            {amountFrom.errorMessage}
                        </Text>
                        {isShowIcon && getErrorInfoIcon()}
                    </Helper>
                );
            } else {
                const newAmount = parseFloat(currentAmount) + parseFloat(amountFromValue);
                return (
                    <Helper noPadding>
                        <Text size="secondary" color="secondary">
                            {resolveArguments(newBalance, getLocalizedRawAmount(lang, country, newAmount))}
                            <Text size="secondary" color="secondary" smallCaps>
                                {' ' + amountCurrency}
                            </Text>
                        </Text>
                    </Helper>
                );
            }
        } else {
            return false;
        }
    };

    const onProcessLinkClick = (value) => {
        const newField = {
            ...props.amountFrom,
            value: String(value),
            isError: false,
            errorMessage: '',
        };
        props.changeFormField(props.formName, newField);
    };

    const getErrorInfoIcon = () => {
        const amountFrom = props.amountFrom;
        return (
            <DiskButton
                size="small"
                iconName="info-rounded"
                iconColor="error"
                type="button"
                raised={false}
                title={amountFrom.errorMessage}
                onClick={(e) => {
                    e.preventDefault();
                    popupRef.current?.open(<TopUpLimitsPopup popupRef={popupRef} onProcessLinkClick={onProcessLinkClick} />);
                }}
            />
        );
    };

    return (
        <>
            <CalculatorFrame>
                <CalculatorFrameTop>
                    <CalculatorAmountBlock>
                        <CalculatorInputLabel size="secondary" color="secondary">
                            {topUpPayTitle}
                        </CalculatorInputLabel>
                        <InputAmount
                            field={props.amountFrom}
                            formName={props.formName}
                            classNameError={props.amountFrom?.isError}
                            minAmount={minAmount}
                            maxAmount={maxAmount}
                            validateOnBlur={false}
                            minAmountErrorKey={'site.js.ga.validation.min.valid.error'}
                            maxAmountErrorKey={'site.js.ga.validation.max.valid.error'}
                            enabledValidateMinMaxAmount={true}
                            currencyCode={account.pocketCurrency.code}
                            isNewInput
                            isCalculatorInput
                            setAmountFromFloatValue={setAmountFromFloatValue}
                        />
                        {getSecondaryText()}
                    </CalculatorAmountBlock>
                </CalculatorFrameTop>
                <CalculatorFrameBottom>{getLabel()}</CalculatorFrameBottom>
            </CalculatorFrame>
            <PopupNew ref={popupRef} />
        </>
    );
};

const mapStateToProps = (state, ownProps) => {
    return {
        amountFrom: state.form[ownProps.formName].fields[ownProps.amountFieldName],
        fields: state.form[ownProps.formName].fields,
    };
};
export default connect(mapStateToProps, { toggleModal, changeFormField })(TopUpCalc);
